/**
 * <related-products> — Relewise-powered variant of related-products.js.
 * Loaded INSTEAD of related-products.js when theme setting "Enable Relewise"
 * is on (see sections/related.liquid); defines the same element with the same
 * markup contract, so the section works identically from the outside.
 *
 * Product selection comes from Relewise (PurchasedWithProduct, display
 * location "Product Details Page"); rendering stays with Shopify — each
 * recommended handle is fetched as /products/<handle>?view=relewise-card so
 * cards use the theme's own product-grid-item markup. Any failure (Relewise
 * off/unreachable, product missing its relewise_id) falls back to Shopify's
 * native /recommendations/products, identical to the original file.
 */
!(function () {
  'use strict';

  const selectors = {
    apiRelatedTemplate: '[data-api-related-template]',
    productItem: '.product-item',
    gridItem: '[data-grid-item]',
    relatedSection: '[data-related-section]',
    recentlyViewed: 'recently-viewed',
    gridSlider: 'grid-slider',
    recentlyViewedWrapper: '[data-recently-viewed-wrapper]',
  };

  const attributes = {
    limit: 'data-limit',
    productId: 'data-product-id',
    relewiseId: 'data-relewise-id',
    layout: 'data-layout',
  };

  const classes = {
    hidden: 'hidden',
    mobileSlider: 'grid--mobile-slider',
    slider: 'grid--slider',
    sliderDisabled: 'grid--slider-disabled',
  };

  if (customElements.get('related-products')) return;

  customElements.define(
    'related-products',
    class RelewiseRelatedProducts extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.id;
        this.relatedItems = 0;
        this.wrapper = this.querySelector(selectors.recentlyViewedWrapper);
        this.recentlyViewed = this.querySelector(selectors.recentlyViewed);
      }

      connectedCallback() {
        this.loadRelatedProducts();
        this.loadRecentlyViewedProducts();
      }

      async loadRelatedProducts() {
        const section = this.querySelector(selectors.relatedSection);
        if (!section) return;

        const cfg = window.spejder && window.spejder.relewise;
        const relewiseId = section.getAttribute(attributes.relewiseId);

        if (!cfg || !cfg.enabled || !window.Relewise || !relewiseId) {
          this.loadShopifyRelated(section);
          return;
        }

        try {
          const limit = parseInt(section.getAttribute(attributes.limit)) || 4;
          const user = await this.getUser();
          const settings = {
            language: cfg.language,
            currency: cfg.currency,
            displayedAtLocation: 'Product Details Page',
            user: user,
          };

          const request = new Relewise.PurchasedWithProductBuilder(settings)
            .product({ productId: relewiseId })
            .setNumberOfRecommendations(limit)
            .filters((f) => f.addProductIdFilter(relewiseId, true))
            .setSelectedProductProperties({ dataKeys: [cfg.shop + '_ShopifyHandle'] })
            .build();

          /* Joins the page's other rows ("Du har kigget på", any placed
             recommendation section) in ONE batched request, deduplicated
             across rows by Relewise — see assets/relewise-batch.js. */
          const result = window.spejder.relewiseBatch
            ? await window.spejder.relewiseBatch.recommend(request)
            : await new Relewise.Recommender(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl }).recommendPurchasedWithProduct(request);

          const handleKey = cfg.shop + '_ShopifyHandle';
          const handles = ((result && result.recommendations) || [])
            .map((rec) => rec.data && rec.data[handleKey] && rec.data[handleKey].value)
            .filter(Boolean);

          if (!handles.length) {
            this.hideSection(section);
            this.updateVisibility();
            return;
          }

          const cards = (
            await Promise.all(
              handles.map((handle) =>
                fetch(`/products/${handle}?view=relewise-card`)
                  .then((response) => (response.ok ? response.text() : ''))
                  .catch(() => '')
              )
            )
          ).filter(Boolean);

          this.renderCards(section, cards);
        } catch (error) {
          this.loadShopifyRelated(section);
        }
      }

      /* Consent-correct identity from the Relewise app embed; anonymous when absent. */
      getUser() {
        /* Shared helper (assets/relewise-batch.js) waits for the app embed's global;
           testing for it synchronously here always found nothing (2026-09-16). */
        if (window.spejder && window.spejder.relewiseUser) return window.spejder.relewiseUser();
        if (window.ShopifyRelewise && typeof window.ShopifyRelewise.getUserAsync === 'function') {
          return window.ShopifyRelewise.getUserAsync().catch(() => Relewise.UserFactory.anonymous());
        }
        return Promise.resolve(Relewise.UserFactory.anonymous());
      }

      renderCards(section, cards) {
        if (!cards.length) {
          this.hideSection(section);
          this.updateVisibility();
          return;
        }

        section.innerHTML = `
          <div class="grid-outer">
            <grid-slider align-arrows>
              <div class="grid ${classes.slider}${cards.length <= 4 ? ` ${classes.sliderDisabled}` : ''}" data-grid-slider id="product-recommendations">
                ${cards.join('')}
              </div>
            </grid-slider>
          </div>`;

        const count = section.querySelectorAll(selectors.gridItem).length;
        if (!count) {
          this.hideSection(section);
          this.updateVisibility();
          return;
        }
        this.relatedItems = count;

        const columnsMobile = parseInt(section.style.getPropertyValue('--COLUMNS-MOBILE'));
        const firstItem = section.querySelector(selectors.gridItem);
        if (columnsMobile === 0) firstItem.parentElement.classList.add(classes.mobileSlider);
        if (section.getAttribute(attributes.layout) === 'slider') {
          firstItem.parentElement.classList.add(classes.slider);
        } else {
          firstItem.parentElement.classList.remove(classes.slider);
        }

        // Cards render outside the section's AOS timeline — unhide them directly.
        section.querySelectorAll('[data-aos]').forEach((el) => el.classList.add('aos-animate'));

        this.settle(section);
        this.updateVisibility();
      }

      /* The section server-renders skeleton shells under [data-relewise-hold]
         (sections/related.liquid). Every outcome releases the hold; an outcome
         without cards also drops the shells, which nothing else replaces. */
      settle(section, empty) {
        if (empty) section.querySelectorAll('[data-relewise-skeleton-card]').forEach((el) => el.remove());
        section.setAttribute('data-relewise-ready', '');
      }

      /* Verbatim behavior of related-products.js — used as fallback. */
      loadShopifyRelated(section) {
        const productId = section.getAttribute(attributes.productId);
        const limit = section.getAttribute(attributes.limit);
        const url = `${window.theme.routes.product_recommendations_url}?section_id=api-product-recommendation&limit=${limit}&product_id=${productId}&intent=related`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => this.handleShopifyRelatedResponse(text, section))
          .catch(() => this.hideSection(section));
      }

      handleShopifyRelatedResponse(text, section) {
        const container = document.createElement('div');
        container.innerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector(selectors.apiRelatedTemplate).innerHTML;

        const count = container.querySelectorAll(selectors.gridItem).length;
        if (count > 0) {
          section.innerHTML = container.innerHTML;
          this.relatedItems = count;

          const columnsMobile = parseInt(section.style.getPropertyValue('--COLUMNS-MOBILE'));
          const firstItem = section.querySelector(selectors.gridItem);
          if (columnsMobile === 0) firstItem.parentElement.classList.add(classes.mobileSlider);
          if (section.getAttribute(attributes.layout) === 'slider') {
            firstItem.parentElement.classList.add(classes.slider);
          } else {
            firstItem.parentElement.classList.remove(classes.slider);
          }
          this.settle(section);
        } else {
          this.hideSection(section);
        }
        this.updateVisibility();
      }

      loadRecentlyViewedProducts() {
        if (!this.recentlyViewed) return;
        this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', () => {
          this.handleRecentlyViewedResponse();
        });
      }

      handleRecentlyViewedResponse() {
        const minimum = parseInt(this.recentlyViewed.dataset.minimum) || 1;
        const items = this.recentlyViewed.querySelectorAll(selectors.productItem);
        const slider = this.recentlyViewed.querySelector(selectors.gridSlider);
        const showWithoutWrapper = !this.wrapper && items.length > 0;
        const showWithWrapper = this.wrapper && items.length >= minimum;

        if (showWithoutWrapper || showWithWrapper) {
          if (showWithWrapper) this.wrapper.classList.remove(classes.hidden);
          this.recentlyViewed.classList.remove(classes.hidden);
          this.recentlyViewed.dispatchEvent(new CustomEvent('theme:tab:check', { bubbles: true }));
          if (slider) slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
        }
        this.updateVisibility();
      }

      hideSection(section) {
        this.settle(section, true);
        section.dispatchEvent(new CustomEvent('theme:tab:hide', { bubbles: true }));
      }

      updateVisibility() {
        const empty = Shopify.Products.getConfig().howManyToShow < 1 && this.relatedItems < 1;
        this.classList.toggle(classes.hidden, empty);
      }
    }
  );
})();
